import React, { useState, useCallback, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Global, css } from '@emotion/core'
import { isEmpty } from 'ramda'
import { useSelector } from 'hooks/redux'
import {
  getDashVehicleSerialNumber,
  getVehicleSubType,
  getDashComponentsList,
} from 'selectors/dash'
import { Row, Col } from 'components/patterns'
import strings from 'utils/strings'
import { CHECPLUS_MODULE_NAMES } from 'utils/constants'
import DividerPipe from 'components/DividerPipe'
import SwitchDetailCard from 'components/vehicles/Summary/ChecPlusVehicleSummary/AddRemoveVehicleFeatures/SwitchDetailCard'
import withStyle, {
  StyledReplaceSwitchModal,
  ModalContent,
  ModalContainer,
  HeaderContainer,
  HeaderTitle,
  HeaderLinkIcon,
  CloseButton,
  CloseIcon,
  SwitchListContainer,
  SideMenu,
  SignalNameHeader,
  SignalInfoText,
  WarningContainer,
  WarningText,
  WarningAlertIcon,
  EmptyState,
  SelectedSwitchContainer,
  SelectedSwitchText,
  SelectedCheckIcon,
} from './ReplaceSwitchModal.style'

// PBI 147363 - Documentation 9: Global styles for update switch modal
const modalStyles = css`
  .update-switch-modal .ant-modal-close {
    display: none !important;
  }

  .update-switch-modal .ant-modal-title {
    width: 100%;
  }

  .update-switch-modal .ant-modal-footer {
    display: flex;
    height: 72px;
    padding: 0 25px;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
    align-self: stretch;
    border-radius: 0 0 3px 3px;
    border-top: 1px solid #e3e5e8;
    background: #fff;
    box-sizing: border-box;

    .ant-btn + .ant-btn {
      margin-left: 0;
    }
  }

  .ant-modal-mask {
    background: rgba(7, 7, 15, 0.7) !important;
  }
`

const stripSignalType = (name) =>
  name ? name.replace(/\s*\([^)]*\)\s*$/, '') : ''

// PBI 147363 - Documentation 9: Side panel card header
const SignalCardHeader = ({ signalName, serialNumber, subType }) => (
  <>
    <SignalNameHeader>{stripSignalType(signalName)}</SignalNameHeader>
    <SignalInfoText>
      {serialNumber} <DividerPipe /> {subType} <DividerPipe />{' '}
      {CHECPLUS_MODULE_NAMES.ASAM}
    </SignalInfoText>
  </>
)

SignalCardHeader.propTypes = {
  signalName: PropTypes.string,
  serialNumber: PropTypes.string,
  subType: PropTypes.string,
}

// PBI 147363 - Documentation 9/10: Side panel card content showing warning or selected switch info
const SignalCardContent = ({ selectedSwitch }) => {
  if (!selectedSwitch) {
    return (
      <WarningContainer>
        <WarningAlertIcon />
        <WarningText>{strings.signal_requires_switch}</WarningText>
      </WarningContainer>
    )
  }

  // PBI 147363 - Documentation 10: Show green checkmark and description when selected
  return (
    <SelectedSwitchContainer>
      <SelectedCheckIcon />
      <SelectedSwitchText>
        {stripSignalType(selectedSwitch.panelDescription)}
      </SelectedSwitchText>
    </SelectedSwitchContainer>
  )
}

SignalCardContent.propTypes = {
  selectedSwitch: PropTypes.shape({
    panelNumber: PropTypes.string,
    panelDescription: PropTypes.string,
    type: PropTypes.string,
  }),
}

/**
 * PBI 147363 - Documentation 9: Update Switches Large Modal
 *
 * This modal allows users to change the switch used on the dash.
 * It presents all compatible switches and shows the signal(s) that
 * will be affected by the switch change.
 */
const ReplaceSwitchModal = ({
  isOpen,
  signalGroups,
  initialSignalName,
  onReplace,
  onCancel,
}) => {
  const initialTabIndex = useMemo(() => {
    const idx = signalGroups.findIndex(
      (g) => g.signalName === initialSignalName
    )
    return idx >= 0 ? idx : 0
  }, [signalGroups, initialSignalName])

  const [selectedTabIndex, setSelectedTabIndex] = useState(initialTabIndex)
  const [selectedSwitches, setSelectedSwitches] = useState({})

  // Reset state when modal opens or initial signal changes
  useEffect(() => {
    if (isOpen) {
      const idx = signalGroups.findIndex(
        (g) => g.signalName === initialSignalName
      )
      setSelectedTabIndex(idx >= 0 ? idx : 0)
      setSelectedSwitches({})
    }
  }, [isOpen, initialSignalName, signalGroups])

  const activeGroup = signalGroups[selectedTabIndex]

  const serialNumber = useSelector(getDashVehicleSerialNumber)
  const subType = useSelector(getVehicleSubType)

  const handleTabClick = useCallback((index) => {
    setSelectedTabIndex(index)
  }, [])

  const handleSwitchSelect = useCallback(
    (panelNumber) => {
      if (!activeGroup) return
      const selected = activeGroup.switches.find(
        (sw) => sw.panelNumber === panelNumber
      )
      if (selected) {
        setSelectedSwitches((prev) => ({
          ...prev,
          [activeGroup.signalName]: selected,
        }))
      }
    },
    [activeGroup]
  )

  const handleConfirm = () => {
    if (Object.keys(selectedSwitches).length > 0) {
      onReplace(selectedSwitches)
    }
  }

  const handleCancel = () => {
    setSelectedSwitches({})
    setSelectedTabIndex(initialTabIndex)
    onCancel()
  }

  // PBI 147363 - Doc 9: Build side panel cards for each signal tab
  const signalCards = useMemo(
    () =>
      signalGroups.map((group, index) => ({
        key: `signal-${group.signalName}`,
        header: (
          <SignalCardHeader
            signalName={group.signalName}
            serialNumber={group.currentPanelNumber}
            subType={subType}
          />
        ),
        content: (
          <SignalCardContent
            selectedSwitch={selectedSwitches[group.signalName]}
          />
        ),
        onClick: () => handleTabClick(index),
        selected: selectedTabIndex === index,
      })),
    [signalGroups, subType, selectedSwitches, selectedTabIndex, handleTabClick]
  )

  // PBI 486277: Collect ALL switches currently on vehicle to prevent duplicates
  const allDashComponents = useSelector(getDashComponentsList)
  const panelNumbersOnVehicle = useMemo(
    () =>
      new Set(
        allDashComponents
          .filter((comp) => comp.panelNumber)
          .map((comp) => comp.panelNumber)
      ),
    [allDashComponents]
  )

  // PBI 147363 - Doc 9: Render switch cards for the active tab
  const renderSwitchCards = useMemo(() => {
    if (!activeGroup || isEmpty(activeGroup.switches)) {
      return (
        <EmptyState>
          <p>{strings.no_alternative_switches_available}</p>
        </EmptyState>
      )
    }
    const sortedSwitches = [...activeGroup.switches].sort((a, b) =>
      (a.panelNumber || '').localeCompare(b.panelNumber || '')
    )
    return (
      <Row gutter={[16, 16]} style={{ rowGap: 16 }}>
        {sortedSwitches.map((switchData) => {
          const { panelNumber, panelDescription, type } = switchData
          const isSelected =
            selectedSwitches[activeGroup.signalName]?.panelNumber ===
            panelNumber
          return (
            <Col span={12} key={panelNumber}>
              <SwitchDetailCard
                className={`switch-${panelNumber}`}
                panelNumber={panelNumber}
                handleSwitchSelection={handleSwitchSelect}
                selectedPanelNumber={isSelected ? panelNumber : null}
                panelDescription={panelDescription}
                type={type}
                disabled={panelNumbersOnVehicle.has(panelNumber)}
              />
            </Col>
          )
        })}
      </Row>
    )
  }, [activeGroup, selectedSwitches, handleSwitchSelect, panelNumbersOnVehicle])

  if (!isOpen) return null

  return (
    <>
      <Global styles={modalStyles} />
      <StyledReplaceSwitchModal
        header={
          <HeaderContainer>
            <HeaderLinkIcon />
            <HeaderTitle>
              {strings.update_switches_to_use_on
                ? strings.update_switches_to_use_on(serialNumber)
                : `Update switches to use on ${serialNumber}`}
            </HeaderTitle>
            <CloseButton onClick={handleCancel} aria-label="Close modal">
              <CloseIcon icon="cancel" fill="darkSlate" />
            </CloseButton>
          </HeaderContainer>
        }
        isOpen={isOpen}
        className="update-switch-modal"
        size="extraLarge"
        customButtons={[
          {
            text: strings.cancel,
            onClick: handleCancel,
            type: 'secondary',
          },
          {
            text: strings.update || 'Update',
            onClick: handleConfirm,
            disabled: Object.keys(selectedSwitches).length === 0,
            type: 'primary',
          },
        ]}
      >
        <ModalContent>
          <ModalContainer>
            <SwitchListContainer>{renderSwitchCards}</SwitchListContainer>
            <SideMenu cards={signalCards} />
          </ModalContainer>
        </ModalContent>
      </StyledReplaceSwitchModal>
    </>
  )
}

ReplaceSwitchModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  signalGroups: PropTypes.arrayOf(
    PropTypes.shape({
      signalName: PropTypes.string,
      currentPanelNumber: PropTypes.string,
      currentLocation: PropTypes.string,
      switches: PropTypes.arrayOf(
        PropTypes.shape({
          switchId: PropTypes.string,
          panelNumber: PropTypes.string,
          panelDescription: PropTypes.string,
          type: PropTypes.string,
        })
      ),
    })
  ),
  initialSignalName: PropTypes.string,
  onReplace: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

ReplaceSwitchModal.defaultProps = {
  signalGroups: [],
  initialSignalName: '',
}

export default withStyle(ReplaceSwitchModal)
